import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Box, Button, Grid, Icon, Typography } from "@mui/material";
import Header from "src/Component/content-component/Header";
import { useNavigate } from "react-router";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import CustomCircularProgress from "src/Component/custom/CustomCircularProgress";
import ButtonComponent from "src/Component/content-component/ButtonComponent";
import MainHeader from "src/Component/content-component/MainHeader";
import default_profileImg from "../../asset/default_profileImg.jpeg";
import axios from "axios";

// Custom component to add small bars at each milestone
const CustomBars = ({ x, y, width, height }) => {
  const barHeight = height * 1;
  const barYPosition = y + height - barHeight;

  return (
    <rect
      x={x - width / 4}
      y={barYPosition}
      width={width / 2}
      height={barHeight}
      fill="#00999E"
      opacity={0.3}
      stroke="#ffffff"
      strokeWidth={3}
    />
  );
};

function ProgressChart({ chartData, isLoading }) {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [isenrolled, setIsEnrolled] = useState(false);
  const [enrollmentStatus, setEnrollmentStatus] = useState("pending");
  const [profileImage, setProfileImage] = useState("");

  const token = sessionStorage.getItem("authToken");

  const handleBack = () => {
    navigate(-1);
  };

  // -------------------------------
  // UPDATED MILESTONES (Backend Driven)
  // -------------------------------
  const fullMilestones =
    chartData?.chartData?.map((item) => ({
      name: item?.name,
      progress: item?.progress,
      status: item?.status,
    })) || [];

  // Current progress from backend
  const currentProgress = chartData?.chart || 0;

  // Filter progress line only up to current point
  const progressLineData = fullMilestones.filter(
    (item) => item.progress <= currentProgress,
  );

  useEffect(() => {
    const storedUsername = sessionStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername);
    }
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}student/get-student-web`,
          {
            headers: {
              Authorization: token,
            },
          },
        );
        const data = response.data.data;

        setIsEnrolled(data.isenrolled);
        setEnrollmentStatus(data.enrollmentStatus || "");
        setProfileImage(data.image || default_profileImg);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, [token]);

  return (
    <>
      <Box sx={{ height: "auto" }}>
        <Grid
          container
          sx={{ height: { xs: "auto", md: "550px" }, py: { xs: 4, md: 0 } }}
        >
          {/* Text Section */}
          <Grid
            item
            md={6}
            xs={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              px: { xs: 2, sm: 4 },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <MainHeader
                content="WELCOME"
                fontSize={{ xs: "40px", sm: "60px", md: "54px", lg: "60px" }}
              />
              <MainHeader
                content={username?.toUpperCase()}
                fontSize={{ xs: "40px", sm: "60px", md: "54px", lg: "60px" }}
                color="#454545"
              />
              <Typography
                sx={{
                  py: "16px",
                  color: "#757575",
                  fontWeight: 550,
                  fontSize: { xs: "18px", sm: "20px", md: "24px" },
                }}
              >
                We're Excited To Help You On Your Journey Towards Academic
                Success
              </Typography>

              {!isenrolled && enrollmentStatus !== "pending" && (
                <>
                  <Typography
                    sx={{
                      pb: "20px",
                      color: "#00999E",
                      fontWeight: 500,
                      fontSize: { xs: "18px", sm: "20px", md: "24px" },
                    }}
                  >
                    Please Complete The Enrollment Form
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "center", md: "flex-start" },
                    }}
                  >
                    <ButtonComponent
                      text="Enroll Now"
                      onClick={() =>
                        navigate("/dashboard/enrollment/enrollment-form")
                      }
                      icon={
                        <Icon
                          icon="eva:arrow-forward-fill"
                          style={{ color: "#fff" }}
                        />
                      }
                    />
                  </Box>
                </>
              )}
            </Box>
          </Grid>

          {/* Image Section */}
          <Grid item md={6} xs={12}>
            <Box
              sx={{
                width: { xs: "280px", sm: "100%", md: "400px", lg: "560px" },
                height: { xs: "280px", sm: "100%", md: "200px", lg: "560px" },
                mx: { xs: "auto", md: "0" },
                position: "relative",
                mt: { xs: 4, md: 0 },
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  right: { xs: 0 },
                  width: { xs: "100%", sm: "90%", md: "80%" },
                  maxWidth: "1149px",
                  height: { xs: "280px", sm: "350px", md: "505px" },

                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#00999E54",
                  mx: "auto",
                  p: { xs: 2, md: 4 },
                  borderRadius: "10px",
                  border: "1px solid #999999",
                }}
              >
                <Box
                  sx={{
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "1px solid #00999E",
                    textAlign: "center",
                    width: {
                      xs: "220px",
                      sm: "270px",
                      md: "270px",
                      lg: "320px",
                    },
                    height: {
                      xs: "220px",
                      sm: "270px",
                      md: "270px",
                      lg: "320px",
                    },
                  }}
                >
                  <img
                    src={
                      profileImage === null ? default_profileImg : profileImage
                    }
                    alt="profile_pic"
                    width="100%"
                    height="100%"
                    style={{ objectFit: "cover" }}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          width: "100%",
          backgroundColor: "#E0F7FA",
          padding: "20px",
          borderRadius: "10px",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            m: "10px 0px",
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          {/* Back Button */}
          <Button
            onClick={handleBack}
            startIcon={<NavigateBeforeRoundedIcon fontSize="18px" />}
            sx={{
              backgroundColor: "#00999E",
              color: "#ffffff",
              "&:hover": {
                color: "#00999E",
                backgroundColor: "#f0f0f0",
              },
              fontWeight: 600,
              borderRadius: "5px",
              minWidth: { xs: "80px", md: "100px" },
              py: { xs: 1, md: 1.2 },
              fontSize: { xs: "0.8rem", md: "1rem" },
              left: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            Back
          </Button>

          <Box sx={{ flexGrow: 1, textAlign: "center" }}>
            <Header content={"My Progress"} />
          </Box>
        </Box>

        <Box sx={{ bgcolor: "#ffffff" }}>
          {isLoading ? (
            <CustomCircularProgress />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={fullMilestones}
                margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
              >
                {/* Gradient Fill */}
                <defs>
                  <linearGradient
                    id="colorProgress"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#00999E" />
                    <stop offset="100%" stopColor="#ffffff" />
                  </linearGradient>
                </defs>

                <XAxis dataKey="name" tick={{ fill: "#00999E" }} />
                <Tooltip />

                {/* Progress Area */}
                <Area
                  type="monotone"
                  data={progressLineData}
                  dataKey="progress"
                  stroke="#00999E"
                  fill="url(#colorProgress)"
                  strokeWidth={4}
                  dot={({ cx, cy }) => (
                    <g>
                      <CustomBars x={cx} y={cy} width={50} height={250 - cy} />
                      <circle
                        cx={cx}
                        cy={cy}
                        r={10}
                        fill="#00999E"
                        stroke="#FFFFFF"
                        strokeWidth={2}
                      />
                    </g>
                  )}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Box>

        <Box sx={{ m: "10px 0px" }}>
          <Header
            content={"Track Your Study Abroad Journey"}
            fontSize={"18px"}
          />
        </Box>
      </Box>
    </>
  );
}

export default ProgressChart;
